import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { parse, isValid, format } from 'date-fns';
import { useForm } from 'react-hook-form';
import { FiChevronDown, FiChevronRight, FiFilter } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { Button, Col, Form, FormGroup, Input, Label, Row } from 'reactstrap';
import { Tooltip } from '@material-ui/core';
import styled from 'styled-components';

import CustomFieldsFilterForm from 'views/CRM/shared/components/CustomFieldsFilterForm';
import DefaultFieldsFilterForm from './DefaultFieldsFilterForm';
import { buttonStyle } from 'components/Containers/ConfigurationContainer/components/utils';
import { useMemorizeFilters } from '../../../../../../../../hooks/useMemorizeInputsFilters';
import { AiOutlineClear } from 'react-icons/ai';
import { resetToFirstDayOfMonth } from '../../../../../../shared/helpers/getFirstDayOfTheMonth';
import { resetFields } from '../../../../../../shared/helpers/resetInputsFIeldsValues';
import { ImageButton } from 'assets/styles/image_style';
import { ImageSize } from 'assets/styles/image_style';
import { toast } from 'react-toastify';
import { saveAs } from 'file-saver';
import productService from 'views/CRM/Product/services/productService';
import Loader from 'react-loader-spinner';
import xlsxImage from '../../../../../../../../assets/img/xlsx.png';
import pdfImage from '../../../../../../../../assets/img/pdf.png';

const CustomFieldsFilterToggle = styled.div`
  display: inline-block;
  margin-bottom: 1rem;
  user-select: none;
  :hover {
    cursor: pointer;
    text-decoration: underline;
  }
`;

const FilterProductsForm = ({
  handleSearch,
  customFields,
  isLoading,
  selectedRecordTypeId,
  setSelectedRecordType,
  recordTypes,
  products,
  reloadProducts
}) => {
  const [showCustomFieldsFilters, setShowCustomFieldsFilters] = useState(false);
  const [showDateForm, setShowDateForm] = useState(false);
  const [analyticalData, setAnalyticalData] = useState([]);
  const [loadingCSV, setLoadingCSV] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [showDisabled, setShowDisable] = useState(false);
  const limitRows = 40000;
  const { getMemorizedFilters, memorizeFilters, clearMemorizedFilters } =
    useMemorizeFilters('CRM - Product');

  const { setValue, control, getValues, handleSubmit, register, reset, watch } =
    useForm();
  const systemConfiguration = useSelector((state) => state.systemConfiguration);

  useEffect(() => {
    if (products) {
      setAnalyticalData(products);
    }
  }, [products]);

  const resetConfig = useMemo(
    () => ({
      dateBegin: resetToFirstDayOfMonth,
      dateEnd: () => new Date().toLocaleDateString('pt-br').toString(),
      filterDateBy: () => 'creation',
      createdByLogin: () => '',
      textQuery: () => ''
    }),
    []
  );

  useEffect(() => {
    const filters = getMemorizedFilters();
    if (filters) {
      reset(filters);
    } else if (customFields) {
      customFields.forEach((field) => {
        if (field) {
          setValue(field.name, '');
        }
      });
    }
  }, [
    customFields,
    getMemorizedFilters,
    reset,
    resetConfig,
    setValue,
    showCustomFieldsFilters
  ]);

  const toISOFormat = useCallback((dateTimeString, bar = false) => {
    const [date] = dateTimeString.split(' ');
    const [DD, MM, YYYY] = date.split('/');
    if (bar) return `${YYYY}/${MM}/${DD}`;
    return `${DD}/${MM}/${YYYY}`;
  }, []);

  const saveFiltersWithRecordType = (filters) => {
    const filtersWithRecordType = {
      ...filters,
      recordTypeId: selectedRecordTypeId
    };
    memorizeFilters(filtersWithRecordType);
  };

  const formatData = (data, showDisabled) => {
    if (!showDateForm) {
      const omit = new Set(['dateBegin', 'dateEnd', 'filterDateBy']);
      return {
        ...Object.fromEntries(
          Object.entries(data).filter(([k]) => !omit.has(k))
        ),
        showDisabled
      };
    }

    const parsedDateBegin = data.dateBegin
      ? parse(data.dateBegin, 'dd/MM/yyyy', new Date())
      : undefined;
    const parsedDateEnd = data.dateEnd
      ? parse(data.dateEnd, 'dd/MM/yyyy', new Date())
      : undefined;

    const formattedDateBegin =
      parsedDateBegin && isValid(parsedDateBegin)
        ? format(parsedDateBegin, 'dd/MM/yyyy')
        : data.dateBegin || undefined;
    const formattedDateEnd =
      parsedDateEnd && isValid(parsedDateEnd)
        ? format(parsedDateEnd, 'dd/MM/yyyy')
        : data.dateEnd || undefined;

    return {
      ...data,
      showDisabled,
      dateBegin: formattedDateBegin,
      dateEnd: formattedDateEnd
    };
  };

  const onSubmit = useCallback(
    async (data) => {
      const formattedData = formatData(data, showDisabled);
      saveFiltersWithRecordType(data);
      await handleSearch({ ...formattedData });
    },
    [showDisabled, showDateForm, selectedRecordTypeId]
  );

  const handleReset = () => {
    if (selectedRecordTypeId && recordTypes && recordTypes.length) {
      setSelectedRecordType(recordTypes[0]);
    }
    resetFields(resetConfig, customFields, control);
    clearMemorizedFilters();
  };

  const handleReportExport = (typeExport, e) => {
    e.preventDefault();

    const params = getValues();

    const dto = {
      ...params,
      dateStart: params.dateBegin,
      dateEnd: params.dateEnd,
      recordTypeId: selectedRecordTypeId,
      showDisabled: showDisabled
    };

    if (showDateForm === false) {
      delete dto.dateBegin;
      delete dto.dateEnd;
      delete dto.filterDateBy;
    }

    if (analyticalData.length < limitRows && typeExport == 'XLSX') {
      setLoadingCSV(true);

      productService
        .getExcel(dto)
        .then((res) => {
          if (res.data.size === 0) {
            setLoadingCSV(false);
            toast.info('Nenhum registro foi encontrado!');
          } else {
            saveAs(
              new Blob([res.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel'
              }),
              `relatorio-produtos.xlsx`
            );

            setLoadingCSV(false);
          }
        })
        .catch(() => {
          setLoadingCSV(false);
          toast.info('Nenhum registro foi encontrado!');
        });
    } else if (typeExport == 'XLSX' && analyticalData.length > limitRows) {
      setLoadingCSV(true);

      productService
        .getCsv(dto)
        .then((res) => {
          if (res.data.size === 0) {
            setLoadingCSV(false);
            toast.info('Nenhum registro foi encontrado!');
          } else {
            saveAs(
              new Blob([res.data], { type: res.headers['content-type'] }),
              `realatorio-produtos-${new Date()}.csv`
            );

            setLoadingCSV(false);
          }
        })
        .catch(() => {
          setLoadingCSV(false);
          toast.info('Nenhum registro foi encontrado!');
        });
    } else if (typeExport == 'PDF') {
      setLoadingPdf(true);
      productService
        .getPdf(dto)
        .then((res) => {
          if (res.data.size === 0) {
            toast.info('Nenhum registro foi encontrado!');
            setLoadingPdf(false);
          } else {
            saveAs(
              new Blob([res.data], { type: 'application/pdf' }),
              `realatorio-produtos-${new Date()}.pdf`
            );
          }
          setLoadingPdf(false);
        })
        .catch(() => {
          setLoadingPdf(false);
          toast.info('Nenhum registro foi encontrado!');
        });
    }
  };

  const handleShowDisableds = (value) => {
    setShowDisable(value);
    if (value === false) reloadProducts();
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <div
        style={{
          position: 'absolute',
          right: '1rem',
          top: '2rem'
        }}
      >
        <span
          onClick={() => {
            handleReset();
          }}
          className="small"
          style={{ cursor: 'pointer' }}
        >
          <AiOutlineClear /> Limpar filtros
        </span>
      </div>
      <DefaultFieldsFilterForm
        handleSearch={handleSearch}
        setValue={setValue}
        control={control}
        getValues={getValues}
        register={register}
        showDateForm={showDateForm}
        setShowDateForm={setShowDateForm}
      />

      {customFields && customFields.length > 0 && (
        <>
          <CustomFieldsFilterToggle
            className="small"
            onClick={() => setShowCustomFieldsFilters(!showCustomFieldsFilters)}
          >
            Filtrar por campos customizados{' '}
            {showCustomFieldsFilters ? (
              <FiChevronDown size={18} />
            ) : (
              <FiChevronRight size={18} />
            )}
          </CustomFieldsFilterToggle>

          {showCustomFieldsFilters && (
            <CustomFieldsFilterForm
              toISOFormat={toISOFormat}
              getValues={getValues}
              customFields={customFields}
              setValue={setValue}
              control={control}
              watch={watch}
            />
          )}
        </>
      )}

      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Button
            style={buttonStyle(systemConfiguration.primary_color)}
            type="submit"
            size="md"
            disabled={isLoading}
          >
            <FiFilter />
            <span className="ml-1">Filtrar Produtos</span>
          </Button>

          <Col
            md="6"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end'
            }}
          >
            <button
              id="button_xlsx"
              style={ImageButton}
              onClick={(e) => {
                handleReportExport('XLSX', e);
              }}
              disabled={analyticalData.length && !loadingCSV ? false : true}
            >
              <Tooltip
                title={
                  <p style={{ fontSize: '14px' }}>
                    {analyticalData.length
                      ? `Fazer download do relatório de Leads. Arquivos menores que ${limitRows} registros serão baixados em XLSX, caso contrário serão em CSV`
                      : 'Nenhum registro para realizar download'}
                  </p>
                }
                arrow
              >
                {loadingCSV ? (
                  <Loader
                    type="ThreeDots"
                    visible={true}
                    height="40"
                    width="40"
                  />
                ) : (
                  <img src={xlsxImage} alt="xlsx" style={ImageSize} />
                )}
              </Tooltip>
            </button>
            <button
              style={ImageButton}
              name="pdf"
              onClick={(e) => {
                handleReportExport('PDF', e);
              }}
              disabled={analyticalData.length && !loadingPdf ? false : true}
            >
              <Tooltip
                title={
                  <p style={{ fontSize: '14px' }}>
                    {analyticalData.length
                      ? 'Fazer download do relatório de Leads.'
                      : 'Nenhum registro para realizar download, faça uma filtragem para realizar esta ação'}
                  </p>
                }
                arrow
              >
                {loadingPdf ? (
                  <Loader
                    type="ThreeDots"
                    visible={true}
                    height="40"
                    width="40"
                  />
                ) : (
                  <img src={pdfImage} alt="pdf" style={ImageSize} />
                )}
              </Tooltip>
            </button>
          </Col>
        </div>
        <Row>
          <Col md="4" className="mt-2">
            <FormGroup check>
              <Label check>
                <Input
                  type="checkbox"
                  checked={showDisabled}
                  onChange={() => handleShowDisableds(!showDisabled)}
                />
                <span>Mostrar desabilitados</span>
              </Label>
            </FormGroup>
          </Col>
        </Row>
      </div>
    </Form>
  );
};

export default FilterProductsForm;
